import type { Patient, Reference } from '@medplum/fhirtypes'
import type { Editor } from '@tiptap/core'
import { useContext } from 'react'
import { EditorParticipantContext } from '#src/components/rich-text-editor/EditorParticipantContext.ts'
import type {
  ObservationCaptureTarget,
  ObservationRecordStatus,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useOptionalParticipantReference } from '#src/contexts/current-participant/useOptionalParticipantReference.ts'

// Whom an observation node records for: the editor's live participant when it
// gives one (`null`: none chosen), else the node extension's, else the
// participant in view.
export function useObservationParticipant(
  extensionParticipant?: Reference<Patient>
): Reference<Patient> | null | undefined {
  const fromEditor = useContext(EditorParticipantContext)
  const fromRoute = useOptionalParticipantReference()
  if (fromEditor !== undefined) {
    return fromEditor
  }
  return extensionParticipant ?? fromRoute
}

// Whom and how a node view records, from its extension's options.
export function useObservationCapture(
  options: {
    participant?: Reference<Patient>
    recordStatus?: ObservationRecordStatus
    isTemplate?: boolean
  },
  editor: Editor
): ObservationCaptureTarget {
  const recordFor = useObservationParticipant(options.participant)
  const deferred = options.recordStatus === 'deferred'
  return {
    participant: recordFor ?? undefined,
    // A held answer is the participant's once the note is signed, so a note
    // with no participant yet can still be answered. A template is never
    // answered: its nodes are only configured.
    noParticipant: !!options.isTemplate || (recordFor === null && !deferred),
    recordStatus:
      options.recordStatus === 'deferred'
        ? 'preliminary'
        : options.recordStatus,
    deferred,
    template: !!options.isTemplate,
    editable: editor.isEditable,
  }
}
